import React, { useState } from 'react';
import { 
  MapPin, Plus, Trash2, Edit3, Save, X, DollarSign, 
  CheckCircle, AlertCircle, Eye, EyeOff
} from 'lucide-react';

const LocationsSettings = ({ settings, handleSettingChange, currentCompany }) => {
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({
    name: '',
    deliveryCharge: '0',
    enabled: true,
    isDefault: false,
    category: 'both',
    description: ''
  });
  const [showAddForm, setShowAddForm] = useState(false);

  // Get locations safely
  const getLocationsSafely = () => {
    return Array.isArray(settings.deliveryLocations) ? settings.deliveryLocations : [];
  };

  const locations = getLocationsSafely();

  // Add new location
  const handleAddLocation = () => {
    if (!newLocation.name.trim()) {
      alert('Please enter a location name');
      return;
    }

    const locationToAdd = {
      id: Date.now(),
      name: newLocation.name.trim(),
      deliveryCharge: newLocation.deliveryCharge,
      enabled: newLocation.enabled,
      isDefault: newLocation.isDefault,
      category: newLocation.category,
      description: newLocation.description.trim()
    };

    // If setting as default, remove default from others in same category
    let updatedLocations = [...locations];
    if (locationToAdd.isDefault) {
      updatedLocations = updatedLocations.map(loc => ({
        ...loc,
        isDefault: loc.category === locationToAdd.category ? false : loc.isDefault
      }));
    }

    updatedLocations.push(locationToAdd);
    handleSettingChange('deliveryLocations', updatedLocations);

    // Reset form
    setNewLocation({
      name: '',
      deliveryCharge: '0',
      enabled: true,
      isDefault: false,
      category: 'both',
      description: ''
    });
    setShowAddForm(false);
  };

  // Update location
  const handleUpdateLocation = (locationId, updates) => {
    let updatedLocations = locations.map(loc => 
      loc.id === locationId ? { ...loc, ...updates } : loc
    );

    // If setting as default, remove default from others in same category
    if (updates.isDefault) {
      const updatingLocation = updatedLocations.find(l => l.id === locationId);
      updatedLocations = updatedLocations.map(loc => ({
        ...loc,
        isDefault: (loc.id === locationId) ? true : 
                   (loc.category === updatingLocation.category) ? false : loc.isDefault
      }));
    }

    handleSettingChange('deliveryLocations', updatedLocations);
    setEditingLocation(null);
  };

  // Delete location
  const handleDeleteLocation = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;

    if (window.confirm(`Delete location "${location.name}"?`)) {
      const updatedLocations = locations.filter(loc => loc.id !== locationId);
      handleSettingChange('deliveryLocations', updatedLocations);
    }
  };

  // Toggle location enabled state
  const handleToggleEnabled = (locationId) => {
    const updatedLocations = locations.map(loc =>
      loc.id === locationId ? { ...loc, enabled: !loc.enabled } : loc
    );
    handleSettingChange('deliveryLocations', updatedLocations);
  };

  // Get enabled locations count
  const enabledCount = locations.filter(loc => loc.enabled).length;
  const pickupLocations = locations.filter(loc => loc.category === 'pickup' || loc.category === 'both');
  const dropoffLocations = locations.filter(loc => loc.category === 'dropoff' || loc.category === 'both');

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-400" />
              Delivery Locations Management
            </h4>
            <p className="text-sm text-gray-400 mt-1">
              Configure pickup and drop-off locations with delivery charges
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showAddForm 
                ? 'bg-gray-700 text-gray-300' 
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            {showAddForm ? <X size={16} /> : <Plus size={16} />}
            <span>{showAddForm ? 'Cancel' : 'Add Location'}</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
            <div className="text-orange-300 text-sm font-medium mb-1">Total Locations</div>
            <div className="text-2xl font-bold text-white">{locations.length}</div>
            <div className="text-xs text-gray-400 mt-1">{enabledCount} enabled</div>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
            <div className="text-green-300 text-sm font-medium mb-1">Pickup Locations</div>
            <div className="text-2xl font-bold text-white">{pickupLocations.length}</div>
            <div className="text-xs text-gray-400 mt-1">
              {pickupLocations.filter(l => l.enabled).length} enabled
            </div>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
            <div className="text-blue-300 text-sm font-medium mb-1">Drop-off Locations</div>
            <div className="text-2xl font-bold text-white">{dropoffLocations.length}</div>
            <div className="text-xs text-gray-400 mt-1">
              {dropoffLocations.filter(l => l.enabled).length} enabled
            </div>
          </div>
        </div>

        {/* Add Location Form */}
        {showAddForm && (
          <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-6 mb-6">
            <h5 className="text-white font-medium mb-4 flex items-center gap-2">
              <Plus size={16} className="text-orange-400" />
              Add New Location
            </h5>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Location Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newLocation.name}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., Colombo Airport"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Delivery Charge ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newLocation.deliveryCharge}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, deliveryCharge: e.target.value }))}
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={newLocation.category}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500"
                >
                  <option value="both">Both Pickup & Drop-off</option>
                  <option value="pickup">Pickup Only</option>
                  <option value="dropoff">Drop-off Only</option>
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newLocation.enabled}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                  />
                  <span>Enabled</span>
                </label>
                
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newLocation.isDefault}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, isDefault: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                  />
                  <span>Set as Default</span>
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={newLocation.description}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500"
                  rows="2"
                  placeholder="Additional details about this location..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleAddLocation}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus size={16} />
                <span>Add Location</span>
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewLocation({
                    name: '',
                    deliveryCharge: '0',
                    enabled: true,
                    isDefault: false,
                    category: 'both',
                    description: ''
                  });
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Locations List */}
        {locations.length === 0 ? (
          <div className="text-center py-12 bg-gray-700/20 rounded-lg border border-gray-600">
            <MapPin size={48} className="mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400 text-lg mb-2">No delivery locations configured</p>
            <p className="text-gray-500 text-sm mb-4">
              Add locations to enable delivery charge calculation
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <Plus size={16} />
              <span>Add First Location</span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {locations.map((location) => (
              <div
                key={location.id}
                className={`bg-gray-700/30 border rounded-lg p-4 transition-all ${
                  location.enabled ? 'border-gray-600' : 'border-gray-700 opacity-60'
                }`}
              >
                {editingLocation === location.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-300 mb-2">Location Name</label>
                        <input
                          type="text"
                          defaultValue={location.name}
                          onChange={(e) => {
                            const updatedLocations = locations.map(loc =>
                              loc.id === location.id ? { ...loc, name: e.target.value } : loc
                            );
                            handleSettingChange('deliveryLocations', updatedLocations);
                          }}
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-300 mb-2">Delivery Charge ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          defaultValue={location.deliveryCharge}
                          onChange={(e) => {
                            const updatedLocations = locations.map(loc =>
                              loc.id === location.id ? { ...loc, deliveryCharge: e.target.value } : loc
                            );
                            handleSettingChange('deliveryLocations', updatedLocations);
                          }}
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-300 mb-2">Category</label>
                        <select
                          defaultValue={location.category}
                          onChange={(e) => {
                            const updatedLocations = locations.map(loc =>
                              loc.id === location.id ? { ...loc, category: e.target.value } : loc
                            );
                            handleSettingChange('deliveryLocations', updatedLocations);
                          }}
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="both">Both</option>
                          <option value="pickup">Pickup Only</option>
                          <option value="dropoff">Drop-off Only</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={location.isDefault}
                            onChange={(e) => handleUpdateLocation(location.id, { isDefault: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-600"
                          />
                          <span>Default</span>
                        </label>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-300 mb-2">Description</label>
                        <textarea
                          defaultValue={location.description}
                          onChange={(e) => {
                            const updatedLocations = locations.map(loc =>
                              loc.id === location.id ? { ...loc, description: e.target.value } : loc
                            );
                            handleSettingChange('deliveryLocations', updatedLocations);
                          }}
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
                          rows="2"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingLocation(null)}
                        className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg text-sm"
                      >
                        <Save size={14} />
                        <span>Done</span>
                      </button>
                      <button
                        onClick={() => setEditingLocation(null)}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <MapPin size={18} className={location.enabled ? 'text-orange-400' : 'text-gray-500'} />
                        <h5 className="text-white font-medium text-lg">{location.name}</h5>
                        
                        {location.isDefault && (
                          <span className="bg-orange-900/30 text-orange-300 px-2 py-0.5 rounded-full text-xs border border-orange-700">
                            Default
                          </span>
                        )}
                        
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${
                          location.category === 'both' ? 'bg-blue-900/30 text-blue-300 border-blue-700' :
                          location.category === 'pickup' ? 'bg-green-900/30 text-green-300 border-green-700' :
                          'bg-purple-900/30 text-purple-300 border-purple-700'
                        }`}>
                          {location.category === 'both' ? 'Both' : 
                           location.category === 'pickup' ? 'Pickup' : 'Drop-off'}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <DollarSign size={14} className="text-green-400" />
                          <span className="text-gray-300">
                            Delivery Charge: <span className="text-white font-medium">${location.deliveryCharge}</span>
                          </span>
                        </div>
                      </div>

                      {location.description && (
                        <p className="text-gray-400 text-sm mt-2">{location.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleToggleEnabled(location.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          location.enabled 
                            ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50' 
                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                        }`}
                        title={location.enabled ? 'Disable' : 'Enable'}
                      >
                        {location.enabled ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>

                      <button
                        onClick={() => setEditingLocation(location.id)}
                        className="p-2 bg-blue-900/30 text-blue-400 rounded-lg hover:bg-blue-900/50 transition-colors"
                        title="Edit"
                      >
                        <Edit3 size={16} />
                      </button>

                      <button
                        onClick={() => handleDeleteLocation(location.id)}
                        className="p-2 bg-red-900/30 text-red-400 rounded-lg hover:bg-red-900/50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationsSettings;